import { useState, useCallback } from 'react'
import { exportReportAttendanceSummaryAPI } from '../../../services/report/attendanceSum'

const cleanParams = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== '' && v !== null && v !== undefined
    )
  )

const parseFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null
  const match = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i
  )
  if (!match?.[1]) return null

  try {
    return decodeURIComponent(match[1].replace(/['"]/g, ''))
  } catch {
    return match[1].replace(/['"]/g, '')
  }
}

const isJsonContentType = (contentType) =>
  typeof contentType === 'string' && contentType.toLowerCase().includes('json')

const tryParseJsonFromBlob = async (blob) => {
  try {
    const text = await blob.text()
    return JSON.parse(text)
  } catch {
    return null
  }
}

export default function useExportReportAttendanceSummary() {
  const [isExporting, setIsExporting] = useState(false)

  const exportAttendanceSummary = useCallback(async (params) => {
    setIsExporting(true)
    try {
      // eslint-disable-next-line no-unused-vars
      const { page, limit, ...rest } = params
      const response = await exportReportAttendanceSummaryAPI(cleanParams(rest))

      const contentDisposition = response?.headers?.['content-disposition']
      const headerContentType = response?.headers?.['content-type']

      let fileName = 'attendance_summary.xlsx'
      const dispositionFilename =
        parseFilenameFromContentDisposition(contentDisposition)
      if (dispositionFilename) {
        fileName = dispositionFilename
      } else {
        const start = rest.startDate ? rest.startDate.replaceAll('-', '') : ''
        const end = rest.endDate ? rest.endDate.replaceAll('-', '') : ''
        if (start && end) {
          fileName = `Summary Report ${start}_${end}.xlsx`
        }
      }

      const raw = response?.data ?? response
      if (raw?.result?.url) {
        window.location.href = raw.result.url
        return true
      }

      if (!(raw instanceof Blob)) return false

      const blobContentType = raw.type || headerContentType
      const maybeJson =
        isJsonContentType(blobContentType) ||
        (typeof blobContentType === 'string' && blobContentType.trim() === '')

      if (maybeJson) {
        const parsed = await tryParseJsonFromBlob(raw)
        if (parsed?.result?.url) {
          window.location.href = parsed.result.url
          return true
        }
        return false
      }

      const blob =
        raw.type || headerContentType
          ? raw
          : new Blob([raw], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      return true
    } catch (error) {
      console.error('exportAttendanceSummary error:', error)
      return false
    } finally {
      setIsExporting(false)
    }
  }, [])

  return {
    exportAttendanceSummary,
    isExporting
  }
}
